import { useCallback, useEffect, useState } from 'react';

interface SidebarState {
  isCollapsed: boolean;
  isSavedMenuExpanded: boolean;
}

const STORAGE_KEY = 'syncly-workspace-sidebar-state';

const defaultState: SidebarState = {
  isCollapsed: false,
  isSavedMenuExpanded: true,
};

export function useSidebarState() {
  const [state, setState] = useState<SidebarState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as SidebarState;
      }
    } catch (error) {
      console.error('Failed to load sidebar state:', error);
    }
    return defaultState;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  }, [state]);

  const toggleCollapsed = useCallback(() => {
    setState((prev) => ({ ...prev, isCollapsed: !prev.isCollapsed }));
  }, []);

  const toggleSavedMenu = useCallback(() => {
    setState((prev) => ({ ...prev, isSavedMenuExpanded: !prev.isSavedMenuExpanded }));
  }, []);

  const setCollapsed = useCallback((isCollapsed: boolean) => {
    setState((prev) => ({ ...prev, isCollapsed }));
  }, []);

  const setSavedMenuExpanded = useCallback((isSavedMenuExpanded: boolean) => {
    setState((prev) => ({ ...prev, isSavedMenuExpanded }));
  }, []);

  return {
    isCollapsed: state.isCollapsed,
    isSavedMenuExpanded: state.isSavedMenuExpanded,
    toggleCollapsed,
    toggleSavedMenu,
    setCollapsed,
    setSavedMenuExpanded,
  };
}
